import type {
  SnackbarErrorHandlerFactory,
  SnackbarFactory,
  SnackbarRetryWrapperFactory,
} from '@/features/shell'
import {
  type FetchUserFn,
  type GetCurrentMemberFn,
  type GetCurrentUserFn,
  type GetCurrentWorkspaceFn,
  type GetUserFn,
  type PrimeUserFn,
} from '@/features/iam'
import { type Document } from '@/features/docs/Document'
import { type Observable } from 'rxjs'
import { createInternalFeatureLoader } from '@/utils/feature-loading-util'
import type {
  DocumentsListViewModel,
  DocumentsListViewModelOptions,
} from '@/features/docs/DocumentsListViewModel'
import type { JobGistEvent } from '@/features/jobs/JobGist'
import type { FetchJobGistFn } from '@/features/jobs'
import type {
  DocumentViewModel,
  DocumentViewModelOptions,
} from '@/features/docs/DocumentViewModel'
import type { DocumentLinkingViewModelOptions } from '@/features/docs/components/LinkDocumentsDialog/DocumentLinkingViewModel'
import type { DocumentLinkingViewModel } from '@/features/docs/components/LinkDocumentsDialog/DocumentLinkingViewModel'
import type { DocumentAccess } from '@/features/docs/DocumentAccess'

/**
 * Delegate for showing the rename document dialog.
 */
export type ShowRenameDocumentDialogFn = (
  document: Document,
  signal?: AbortSignal,
) => unknown

/**
 * Prompts for and performs deletion if accepted.
 *
 * @param document
 * @param signal
 * @returns A boolean promise that resolves to true if the
 *  document was deleted, or false if the operation was cancelled or
 *  the user declined.
 */
export type PromptDeleteDocumentFn = (
  document: Document,
  signal?: AbortSignal,
) => Promise<boolean>

/**
 * Prompts for and performs unlinking if accepted.
 *
 * @param document The document to unlink
 * @param access The access to remove
 * @param signal Optional abort signal
 * @returns A promise that resolves to:
 *  - false if the operation was cancelled or the user declined
 *  - 'unlinked' if the document was unlinked
 *  - 'deleted' if the document was deleted
 */
export type PromptUnlinkDocumentFn = (
  document: Document,
  access: DocumentAccess,
  signal?: AbortSignal,
) => Promise<false | 'unlinked' | 'deleted'>

/**
 * Factory for creating a documents list view model.
 */
export type DocumentsListViewModelFactory = (
  options: DocumentsListViewModelOptions,
  signal?: AbortSignal,
) => DocumentsListViewModel

/**
 * Factory for creating a document linking view model.
 */
export type DocumentLinkingViewModelFactory = (
  options: DocumentLinkingViewModelOptions,
) => DocumentLinkingViewModel

/**
 * Factory for creating a document view model.
 */
export type DocumentViewModelFactory = (
  document: Document,
  options: DocumentViewModelOptions,
) => DocumentViewModel

/**
 * Options and dependencies for the documents feature.
 */
export interface DocumentsFeatureOptions {
  token$: Observable<string>
  getCurrentUser: GetCurrentUserFn
  getCurrentWorkspace: GetCurrentWorkspaceFn
  getCurrentMember: GetCurrentMemberFn
  fetchUser: FetchUserFn
  getUser: GetUserFn
  primeUser: PrimeUserFn
  fetchJobGist: FetchJobGistFn
  jobGistRealtime$: Observable<JobGistEvent>
  newSnackbar: SnackbarFactory
  createSnackbarErrorHandler: SnackbarErrorHandlerFactory
  createSnackbarRetryWrapper: SnackbarRetryWrapperFactory
}

/**
 * Feature relating to documents.
 */
export type DocumentsFeature = ReturnType<typeof DocumentsFeature>

export function DocumentsFeature(options: DocumentsFeatureOptions) {
  const internal = createInternalFeatureLoader(
    () => import('./internal'),
    (module) => module.InternalDocumentsFeature(options),
  )

  const showRenameDialog: ShowRenameDocumentDialogFn = internal.wrap(
    (m) => m.showRenameDialog,
  )

  const promptDelete: PromptDeleteDocumentFn = internal.wrap(
    (m) => m.promptDelete,
  )

  const promptUnlink: PromptUnlinkDocumentFn = internal.wrap(
    (m) => m.promptUnlink,
  )

  const createDocumentListViewModel =
    internal.lazy<DocumentsListViewModelFactory>(
      (mod) => mod.createDocumentsListViewModel,
    )

  const createDocumentViewModel = internal.lazy<DocumentViewModelFactory>(
    (mod) => mod.createDocumentViewModel,
  )

  const createDocumentLinkingViewModel =
    internal.lazy<DocumentLinkingViewModelFactory>(
      (mod) => mod.createDocumentLinkingViewModel,
    )

  return {
    // Internal
    internal,

    // Public
    showRenameDialog,
    promptDelete,
    promptUnlink,
    createDocumentListViewModel,
    createDocumentViewModel,
    createDocumentLinkingViewModel,
  }
}
